import React from 'react';
import { Link } from 'react-router-dom';
import HtmlContent from '../../../components/common/HtmlContent';
import useHashScroll from '../../../hooks/useHashScroll';

/**
 * ProblemReframingPage Component
 * 
 * Displays problem reframing tools content like the legacy HTML site
 */
const ProblemReframingPage = () => {
  // Use the hash scroll hook to scroll to the correct section
  useHashScroll();
  
  // Problem Reframing intro content from the legacy HTML
  const problemReframingIntroHtml = `
    <p>
    As we move through the problem discovery phase, and through validating, design thinking encourages us to take a step back and focus on the chosen problem by looking at it from a different perspective, i.e. Reframing.
    </p>

    <p>
    By doing this, we expand the range of solutions that are possible and set the project up well for moving in to ideation and solutions.  
    </p>
    
    <p>
    These tools help in the reframing mindset, enabling a new problem statement to be created with a different perspective.
    </p>
  `;

  // Templates HTML for each tool
  const conceptTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/synthesis/problem-reframing/problem-reframing.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_kpPaIPU=/?" className="link-table">Miro</a>
    </>
  );

  const rootCauseAnalysisTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/synthesis/problem-reframing/root-cause-analysis.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_koRWxOo=/" className="link-table">Miro</a>
    </>
  );

  const reframingMatrixTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/synthesis/problem-reframing/reframing-matrix.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_koRRXxA=/" className="link-table">Miro</a>
    </>
  );

  const challengeMappingTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/synthesis/problem-reframing/challenge-mapping.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_koQBPUs=/" className="link-table">Miro</a>
    </>
  );

  // Creator HTML
  const tomasWedellCreator = <a href="https://hbr.org/2017/01/are-you-solving-the-right-problems/">Thomas Wedell-Wedellsborg</a>;
  const sakichiToyodaCreator = <a href="http://www.thwink.org/sustain/publications/brochures/04_RCA_At_Thwink/RCA_At_Thwink_Pages.pdf">Sakichi Toyoda</a>;
  const morganJonesCreator = <a href="https://www.amazon.com/Thinkers-Toolkit-Powerful-Techniques-Problem/dp/0812928083">Morgan Jones</a>;
  const naCreator = <a href="">N/A</a>;

  // Helper function to render tool info table with optional creator row
  const renderToolInfoTable = (category, updated, templates, creator = null) => {
    return (
      <div className="tool-table-wrap">
        <table id="tool-table">
          <tbody>
            <tr>
              <th colSpan="3">Tool Info</th>
            </tr>
            <tr>
              <td>Category</td>
              <td>{category}</td>  
            </tr>
            <tr>
              <td>Updated</td>
              <td>{updated}</td>
            </tr>
            <tr>
              <td>Templates</td>
              <td>{templates}</td>
            </tr>
            {creator && (
              <tr>
                <td>Creator</td>
                <td>{creator}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      {/* Problem Reframing Intro */}
      <div className="tool-overview-container">
        <div className="tool-breadcrumb-container">
          <div className="tool-breadcrumb-wrap">
            <div className="tool-breadcrumb-icon">
              <i className="fas fa-bullhorn fa-2x"></i>
            </div>
            <div className="tool-breadcrumb-nav">
              <Link to="/research">Research Tools</Link>
              &nbsp;&nbsp;
              <i className="fas fa-angle-double-right"></i>
              &nbsp;&nbsp;
              <Link to="/synthesis">Synthesis</Link>
            </div>
            <div className="tool-breadcrumb-title">
              <Link to="/synthesis/problem-reframing">Problem Reframing</Link>
            </div>
          </div>
        </div>
        <div className="tool-overview-para">
          <HtmlContent html={problemReframingIntroHtml} containerClass="" />
        </div>
        <div className="tool-section-container">
          <div className="tool-section-wrap">
            <div className="tool-section-title">Section Tools</div>
          </div>
          <div className="tool-section-list">
            <div><Link className="tool-section-cta" to="#concept">Concept</Link></div>
            <div><Link className="tool-section-cta" to="#root-cause-analysis">Root Cause Analysis</Link></div>
            <div><Link className="tool-section-cta" to="#reframing-matrix">Reframing Matrix</Link></div>
            <div><Link className="tool-section-cta" to="#challenge-mapping">Challenge Mapping</Link></div>
          </div>
        </div>
      </div>

      {/* Concept Section */}
      <main className="case-study">
        <span className="case-tool-title" id="concept">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="concept">
            <span className="tool-title-number">
              <Link to="#concept">Concept</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/synthesis">Synthesis</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/synthesis/problem-reframing">Problem Reframing</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#concept"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-concept" 
            id="tab-concept-overview" 
            defaultChecked
          />
          <label htmlFor="tab-concept-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Problem Reframing", "30/08/20", conceptTemplates, tomasWedellCreator)}
              <div className="tool-opening">
                <p>
                Understanding the theory behind Problem Reframing really helps to clarify the best approach and how this phase evolves. 
                </p>
            
                <p>
                <span className="press">Are You Solving the Right Problems? - Thomas Wedell-Wedellsborg</span>
                </p>
            
                <p>
                There is a lot of commentary around Problem Reframing, but the best piece I've come across is found on Harvard Business Review, providing excellent examples and pointers as part of the approach, it is a very concise but thorough explanation - I encourage you to read it <a href="https://hbr.org/2017/01/are-you-solving-the-right-problems/">here</a>, and I have also summarised the advised steps and process below.
                </p>
            
                <p>
                <span className="underline">What's the TL;DR?</span>
                </p>    

                <p>
                People are bad at properly diagnosing problems, even when using frameworks that help understand the problem people will just simply go deeper in to this specific problem instead of refocusing and coming up with a different diagnosis.  
                </p>
            
                <p>               
                Ultimately, it's not about discounting the original problem, or even invalidating it, but looking for a more interesting problem - a more valuable problem. 
                </p>              
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://hbr.org/2017/01/are-you-solving-the-right-problems" className="link-table">Are You Solving the Right Problems?</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="http://www.thunderheadworks.com/ten-ways-reframe-problem-challenge/" className="link-table">10 ways to reframe problems… rather, challenges</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.fastcompany.com/1672354/how-reframing-a-problem-unlocks-innovation" className="link-table">How Reframing A Problem Unlocks Innovation</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-concept" 
            id="tab-concept-slow-elevator"
          />
          <label htmlFor="tab-concept-slow-elevator">The Slow Elevator Problem</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_koRKGo8=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="The Slow Elevator Problem"
            ></iframe>
          </div>

          <input 
            type="radio" 
            name="tabs-concept" 
            id="tab-concept-tips"
          />
          <label htmlFor="tab-concept-tips">Tips</label>
          <div className="tab tab-tools">
            <ul className="bullet-tk">
              <li><span className="press">Establish legitimacy</span> - Help everyone to understand the method before using it. This will enable a much better discussion, keeping everyone aligned and resulting in a better outcome.</li>
              <li><span className="press">Bring outsiders into the discussion</span> - Encourages diverse perspectives and moves people away from falling in love with a solution, and falling in love with the problem instead.</li>
              <li><span className="press">Get people's definitions in writing</span> - By getting everyone to agree on a single problem statement, it prevents ambiguity around exactly what is being solved.</li>
              <li><span className="press">Ask what's missing</span> - Instead of focusing on what's included, take the time to understand and investigate what could be missing from the statement.</li>
              <li><span className="press">Consider multiple categories</span> - Get participants to state what category of problem it is (incentive, expectations, attitude etc) and also suggest others for consensus - this is also a good example of where a diverse group helps with understanding the problem. </li>
              <li><span className="press">Analyse positive exceptions</span> - Are there instances where the problem did not occur? These positive exceptions can also help uncover factors of the problem that had not been considered.</li>
              <li><span className="press">Question the objective</span> - Uncovering the objectives that are causing the problem (scenario or user) by clarifying and then challenging them can help shift the objective and therefore reframe the problem.</li>
            </ul>
          </div>

          <input 
            type="radio" 
            name="tabs-concept" 
            id="tab-concept-template"
          />
          <label htmlFor="tab-concept-template">Reframing Template</label>
          <div className="tab tab-tools">
            <p>
            Using a template for the Reframing process will help with consistency of thought - allowing for prompting and practice in the art of Reframing. 
            </p>

            <p>
            Changing keywords, and observing the underlying goals whilst going through this process will help generate the Reframed Problem Statement.
            </p>

            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_kpPaIPU=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Reframing Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Root Cause Analysis Section */}
      <main className="case-study">
        <span className="case-tool-title" id="root-cause-analysis">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="root-cause-analysis">
            <span className="tool-title-number">
              <Link to="#root-cause-analysis">Root Cause Analysis</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/synthesis">Synthesis</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/synthesis/problem-reframing">Problem Reframing</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#root-cause-analysis"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-root-cause-analysis" 
            id="tab-root-cause-analysis-overview" 
            defaultChecked
          />
          <label htmlFor="tab-root-cause-analysis-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Problem Reframing", "30/08/20", rootCauseAnalysisTemplates, sakichiToyodaCreator)}
              <div className="tool-opening">
                <p>
                We started with our problems, we defined, prioritised and framed them ready for validation, now that we've validated the problem through research and user feedback we can begin to understand it further by doing a Root Cause Analysis and potentially reframe the exact problem to observe and solve from a different perspective.                                
                </p>
            
                <p>
                An RCA takes the problem and combines the '5 whys' tool so that we can truly understand the circumstances that lead to the problem manifesting itself. By gathering multiple 'Whys' we can begin to understand the multitude of reasons for this problem manifesting itself. 
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://xtensio.com/root-cause-analysis-for-entrepreneurship/" className="link-table">Root Cause Analysis For Entrepreneurship</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.designorate.com/how-to-apply-root-cause-analysis-using-5-whys/" className="link-table">How to Apply Root Cause Analysis Using 5 Whys</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-root-cause-analysis" 
            id="tab-root-cause-analysis-template"
          />
          <label htmlFor="tab-root-cause-analysis-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_koRWxOo=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Root Cause Analysis Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Reframing Matrix Section */}
      <main className="case-study">
        <span className="case-tool-title" id="reframing-matrix">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="reframing-matrix">
            <span className="tool-title-number">
              <Link to="#reframing-matrix">Reframing Matrix</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/synthesis">Synthesis</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/synthesis/problem-reframing">Problem Reframing</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#reframing-matrix"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-reframing-matrix" 
            id="tab-reframing-matrix-overview" 
            defaultChecked
          />
          <label htmlFor="tab-reframing-matrix-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Problem Reframing", "30/08/20", reframingMatrixTemplates, <a href="https://www.mindtools.com/pages/article/newCT_05.htm">Michael Morgan</a>)}
              <div className="tool-opening">
                <p>
                Continuing with the theme of looking at problems from a different perspective, a Reframing Matrix encourages different people with different experiences to come together as they will approach problems in different ways.
                </p>
            
                <p>
                The technique then examines the problem from four different business perspectives; 

                <ul className="bullet-tk">
                    <li>Product - Is there something wrong with the product? Is there a price issue? Is it reliable?</li>
                    <li>Planning - Are the business, marketing or strategy plans at fault?</li>
                    <li>People - What impact is the problem having on people? Why are potential users not using the product? What do the people involved with the project think?</li>
                    <li>Potential - Could increasing targets, speed and production effect the problem? How can this be achieved?</li>
                </ul>

                </p>

                <p>
                However, it could also be used to examine the problem from four different stakeholder perspectives for example. 
                </p>   

                <p>
                This is quite a transparent exercise, and it's important like all things to not point blame, but to resolve the problem. 
                </p>        

                <p>
                We can use the insights garnered during this process to them update the Problem Statement or Hypothesis, or help define the problem reading for ideation. 
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://link.springer.com/chapter/10.1007/978-981-10-0983-9_31" className="link-table">The Reframing Matrix</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.mindtools.com/pages/article/newCT_05.htm" className="link-table">Using Creative Perspectives to Solve Problems</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-reframing-matrix" 
            id="tab-reframing-matrix-template"
          />
          <label htmlFor="tab-reframing-matrix-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_koQ9oEc=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Reframing Matrix Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Challenge Mapping Section */}
      <main className="case-study">
        <span className="case-tool-title" id="challenge-mapping">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="challenge-mapping">
            <span className="tool-title-number">
              <Link to="#challenge-mapping">Challenge Mapping</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/synthesis">Synthesis</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/synthesis/problem-reframing">Problem Reframing</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#challenge-mapping"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-challenge-mapping" 
            id="tab-challenge-mapping-overview" 
            defaultChecked
          />
          <label htmlFor="tab-challenge-mapping-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Problem Reframing", "30/08/20", challengeMappingTemplates, <a href="https://innovationenglish.sites.ku.dk/metode/challenge-map/">Antropologerne</a>)}
              <div className="tool-opening">
                <p>
                Another tool that can with reframing a problem and looking at things from a different perspective is Challenge Mapping. It encourages different strategies and tactics for solving the problem by reframing the approach.
                </p>
            
                <p>
                This is achieved by a central 'How Might We' question around fixing the problem, and then asking the question 'Why' that will work, creating another 'How Might We' and so on, and then asking 'What's Stopping Us' and creating further 'How Might We' questions.  
                </p>
            
                <p>
                This helps to surface the key decisions points that will have the biggest impact on the problem.  
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.7leaguestudio.com/blog/2019/5/26/challenge-mapping-part-1-challenge-map-basics" className="link-table">Challenge Mapping Part 1 - Challenge Map Basic</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://disruptorleague.com/2015/11/27/how-to-frame-a-problem-using-a-challenge-map/" className="link-table">How to Frame a Problem Using a Challenge Map</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://innovationenglish.sites.ku.dk/metode/challenge-map/" className="link-table">Challenge Map - From fieldwork to insights and development potential</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-challenge-mapping" 
            id="tab-challenge-mapping-template"
          />
          <label htmlFor="tab-challenge-mapping-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_koX_sJo=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Challenge Mapping Template"
            ></iframe>
          </div>
        </div>
      </main>
    </>
  );
};

export default ProblemReframingPage; 